import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SortableTable from '../../../../components/common/SortableTable/SortableTable.jsx';
import Pagination from '../../../../components/common/Pagination/Pagination.jsx';
import { useDateTime } from '../../../../hooks/useDateTime';

// Separate search input component to isolate re-renders
const SearchInput = React.memo(({ value, onChange, placeholder }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="search-input"
    />
));

// Loading overlay component
const LoadingOverlay = React.memo(({ loading }) => 
    loading ? (
        <div className="table-loading-overlay">
            <div className="spinner"></div>
            <span>Loading...</span>
        </div>
    ) : null
);

const MovementTable = React.memo(({
    hookData
}) => {
    const { t } = useTranslation(['inventory', 'common']);
    const { formatDateTime } = useDateTime();

    const getMovementTypeClass = useCallback((type) => {
        switch (type?.toLowerCase()) {
            case 'in':
            case 'inbound':
            case 'purchase':
                return 'movement-in';
            case 'out':
            case 'outbound':
            case 'sale':
                return 'movement-out';
            case 'adjustment':
            case 'adjust':
                return 'movement-adjustment';
            default:
                return 'movement-default';
        }
    }, []);

    const formatMovementType = useCallback((type) => {
        switch (type?.toLowerCase()) {
            case 'in':
            case 'inbound':
            case 'purchase':
                return t('inventory:movementIn');
            case 'out':
            case 'outbound':
            case 'sale':
                return t('inventory:movementOut');
            case 'adjustment':
            case 'adjust':
                return t('inventory:movementAdjustment');
            default:
                return type || '-';
        }
    }, [t]);

    const formatQuantity = useCallback((quantity, type) => {
        const isNegative = type?.toLowerCase() === 'out' || type?.toLowerCase() === 'outbound' || type?.toLowerCase() === 'sale';
        const displayQuantity = Math.abs(quantity || 0);
        const prefix = isNegative ? '-' : '+';
        return `${prefix}${displayQuantity}`;
    }, []);

    // Table columns configuration with proper memoization
    const columns = useMemo(() => [
        {
            key: 'product_name',
            label: t('inventory:product'),
            sortable: true,
            render: (movement) => <strong>{movement.product_name || movement.product?.name || '-'}</strong>
        },
        {
            key: 'movement_type',
            label: t('inventory:movementType'),
            sortable: true,
            render: (movement) => (
                <span className={`movement-badge ${getMovementTypeClass(movement.movement_type)}`}>
                    {formatMovementType(movement.movement_type)}
                </span>
            )
        },
        {
            key: 'quantity',
            label: t('inventory:quantity'),
            sortable: true,
            render: (movement) => (
                <span className={`quantity ${getMovementTypeClass(movement.movement_type)}`}>
                    {formatQuantity(movement.quantity, movement.movement_type)}
                </span>
            )
        },
        {
            key: 'reference_number',
            label: t('inventory:reference'),
            sortable: true,
            render: (movement) => movement.reference_number || '-'
        },
        {
            key: 'username',
            label: t('inventory:user'),
            sortable: true,
            render: (movement) => movement.username || '-'
        },
        {
            key: 'notes',
            label: t('inventory:notes'),
            sortable: false,
            render: (movement) => movement.notes ? (
                <span title={movement.notes}>
                    {movement.notes.length > 30 ? `${movement.notes.substring(0, 27)}...` : movement.notes}
                </span>
            ) : '-'
        },
        {
            key: 'created_at',
            label: t('inventory:date'),
            sortable: true,
            render: (movement) => formatDateTime(movement.created_at)
        }
    ], [t, formatDateTime, getMovementTypeClass, formatMovementType, formatQuantity]);

    // Row styling based on movement type
    const getRowClassName = useCallback((movement) => {
        return getMovementTypeClass(movement.movement_type);
    }, [getMovementTypeClass]);

    // Memoized pagination props
    const paginationProps = useMemo(() => ({
        pagination: hookData.pagination,
        onPageChange: hookData.onPageChange,
        onLimitChange: hookData.onLimitChange,
        limitOptions: hookData.limitOptions
    }), [
        hookData.pagination,
        hookData.onPageChange,
        hookData.onLimitChange,
        hookData.limitOptions
    ]);

    return (
        <div className="inventory-content">
            {/* Search Bar - no add button for movements (read-only) */}
            <div className="action-bar">
                <SearchInput
                    value={hookData.search}
                    onChange={hookData.onSearchChange}
                    placeholder={t('inventory:searchMovements')}
                />
                <div className="action-bar-info">
                    <span className="text-muted">
                        {t('inventory:movementsReadOnly')}
                    </span>
                </div>
            </div>

            {/* Table with loading overlay - isolated re-renders */}
            <div style={{ position: 'relative' }}>
                <SortableTable
                    columns={columns}
                    data={hookData.movements}
                    getRowClassName={getRowClassName}
                    sortField={hookData.sort}
                    sortOrder={hookData.order}
                    onSort={hookData.onSortChange}
                    emptyMessage={hookData.search ? t('inventory:noMovementsSearch') : t('inventory:noMovementsFound')}
                    // No edit/delete actions for movements (read-only)
                />
                <LoadingOverlay loading={hookData.loading} />
            </div>

            {/* Pagination Controls - memoized */}
            {hookData.pagination && (
                <Pagination {...paginationProps} />
            )}
        </div>
    );
});

// Display name for React DevTools
MovementTable.displayName = 'MovementTable';

export default MovementTable;